import {
  Ban,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Network,
  RefreshCw,
  UserPlus,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAdminToken, setAdminToken } from '../../api/client'
import { AdminHeader } from './adminAccess'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { AdminAggregatorOrgRow, AdminAggregatorOverview, AdminAggregatorResponse, ApprovalStatus } from '../../types/api'
import { AGGREGATOR_KINDS } from '../aggregator-portal/aggregatorKinds'

const STATUS_STYLE: Record<ApprovalStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  SUSPENDED: 'bg-red-100 text-red-800',
  REJECTED: 'bg-ink-100 text-ink-600',
}

/** Where a partner stands on aggregator-dpg (V55), with the reason on hover when it did not go through. */
const DPG_LABEL: Record<string, [string, string]> = {
  registered: ['Registered', 'text-green-700'],
  registered_as_aggregator: ['Registered as aggregator', 'text-green-700'],
  waiting_org: ['Waiting for org', 'text-amber-700'],
  exists: ['Already there', 'text-amber-700'],
  unsupported: ['No orgs there', 'text-ink-500'],
  failed: ['Failed', 'text-red-700'],
}

function DpgBadge({ status, error }: { status?: string | null; error?: string | null }) {
  const [label, tone] = (status && DPG_LABEL[status]) || ['Not sent yet', 'text-ink-500']
  return (
    <span className={`text-xs ${tone}`} title={error ?? undefined}>
      {label}
    </span>
  )
}

const kindLabel = (kind: string) => AGGREGATOR_KINDS.find((k) => k.kind === kind)?.label ?? kind
const audienceLabel = (a?: string | null) =>
  a === 'STUDENTS' ? 'Seekers (students)' : a === 'TUTORS' ? 'Providers (tutors)' : 'Students & tutors'

/**
 * Aggregator analysis for ops: how many organisations joined, how many coordinators each has, and how
 * many seekers and tutors each coordinator brought in. Ops can suspend an organisation (stopping its
 * invitations and every coordinator's uploads) or a single coordinator, and retry a coordinator's
 * Signals connection.
 */
export function AdminAggregatorsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<AdminAggregatorOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [open, setOpen] = useState<Record<string, boolean>>({})

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const overview = await adminApi.aggregatorOverview()
      setData(overview)
      // The first organisation opens by default, so there is something to see without a click.
      setOpen((o) => (Object.keys(o).length || !overview.orgs[0] ? o : { [overview.orgs[0].id]: true }))
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        signOut()
        return
      }
      setError(apiErrorMessage(err, 'Could not load aggregators'))
    } finally {
      setLoading(false)
    }
  }, [signOut])

  useEffect(() => {
    if (getAdminToken()) void load()
  }, [load])

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />

  async function act(id: string, action: () => Promise<unknown>, done?: string) {
    setBusyId(id)
    setError(null)
    setNotice(null)
    try {
      const result = await action()
      const networkError = (result as AdminAggregatorResponse | undefined)?.networkError
      if (networkError) setNotice(`Saved, but the Signals network step failed - ${networkError}`)
      else if (done) setNotice(done)
      await load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save that'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="aggregators" onSignOut={signOut} width="max-w-6xl">
        <button onClick={() => void load()} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
            <Network size={18} /> Aggregator analysis
          </h1>
          <p className="text-sm text-ink-500">
            Organisations that joined through the aggregator portal, their coordinators, and the seekers and tutors each
            coordinator brought in.
          </p>
        </div>

        <ErrorMessage message={error} />
        {notice && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{notice}</div>}

        {data && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile icon={<Building2 size={16} />} label="Aggregators joined" value={data.organisations} />
            <Tile icon={<UserPlus size={16} />} label="Coordinators" value={data.coordinators} />
            <Tile icon={<Users size={16} />} label="Students brought in" value={data.studentsOnboarded} />
            <Tile icon={<GraduationCap size={16} />} label="Tutors brought in" value={data.tutorsOnboarded} />
          </div>
        )}

        {loading && !data ? (
          <div className="card-pad text-sm text-ink-500">Loading...</div>
        ) : data && data.orgs.length === 0 && data.unaffiliated.length === 0 ? (
          <div className="card-pad text-sm text-ink-500">
            No aggregator has joined yet. Organisations sign up on the aggregator portal:{' '}
            <a href="/aggregator-portal/register" target="_blank" rel="noopener noreferrer" className="font-semibold text-ink-900 underline">
              /aggregator-portal/register
            </a>
            .
          </div>
        ) : (
          data && (
            <>
              {data.orgs.map((org) => (
                <OrgCard
                  key={org.id}
                  org={org}
                  open={!!open[org.id]}
                  onToggle={() => setOpen((o) => ({ ...o, [org.id]: !o[org.id] }))}
                  busyId={busyId}
                  onSuspendOrg={() => act(org.id, () => adminApi.suspendAggregatorOrg(org.id), `${org.name} suspended`)}
                  onReinstateOrg={() => act(org.id, () => adminApi.reinstateAggregatorOrg(org.id), `${org.name} reinstated`)}
                  act={act}
                />
              ))}

              {data.unaffiliated.length > 0 && (
                <section className="card-pad">
                  <h2 className="font-bold text-ink-900">Coordinators without an organisation</h2>
                  <p className="text-xs text-ink-500">Accounts from before organisations existed on the portal.</p>
                  <CoordinatorTable rows={data.unaffiliated} busyId={busyId} act={act} />
                </section>
              )}
            </>
          )
        )}
      </main>
    </div>
  )
}

function OrgCard({
  org,
  open,
  onToggle,
  busyId,
  onSuspendOrg,
  onReinstateOrg,
  act,
}: {
  org: AdminAggregatorOrgRow
  open: boolean
  onToggle: () => void
  busyId: string | null
  onSuspendOrg: () => void
  onReinstateOrg: () => void
  act: (id: string, action: () => Promise<unknown>, done?: string) => Promise<void>
}) {
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  return (
    <section className="card-pad">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button onClick={onToggle} className="flex min-w-0 flex-1 items-start gap-2 text-left">
          <span className="mt-0.5 text-ink-400">{open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-ink-900">{org.name}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[org.approvalStatus]}`}>{org.approvalStatus}</span>
              <span className="text-xs text-ink-400">Aggregator DPG:</span>
              <DpgBadge status={org.dpgStatus} error={org.dpgSyncError} />
            </span>
            <span className="mt-0.5 block text-sm text-ink-600">
              {kindLabel(org.kind)}
              {org.city ? ` · ${org.city}` : ''} · {org.ownerName} · {org.email}
              {org.phone ? ` · ${org.phone}` : ''}
            </span>
            <span className="mt-1 flex flex-wrap gap-2 text-xs">
              <Count label="Invites sent" value={org.invitesSent ?? 0} />
              <Count label="Coordinators" value={org.coordinators.length} />
              <Count label="Seekers" value={org.studentsOnboarded} />
              <Count label="Tutors" value={org.tutorsOnboarded} />
              <span className="self-center text-ink-400">Joined {new Date(org.createdAt).toLocaleDateString()}</span>
            </span>
          </span>
        </button>
        {org.approvalStatus === 'PENDING' || org.approvalStatus === 'REJECTED' ? (
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <button
                disabled={busyId === org.id}
                onClick={() => void act(org.id, () => adminApi.approveAggregatorOrg(org.id), `${org.name} approved - they have been emailed`)}
                className="btn-sun gap-1.5 disabled:opacity-40"
              >
                <Check size={14} /> Approve
              </button>
              {org.approvalStatus === 'PENDING' && !rejecting && (
                <button disabled={busyId === org.id} onClick={() => setRejecting(true)} className="btn-ghost gap-1.5 disabled:opacity-40">
                  <Ban size={14} /> Reject
                </button>
              )}
            </div>
            {rejecting && (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void act(org.id, () => adminApi.rejectAggregatorOrg(org.id, reason.trim()), `${org.name} rejected - they have been emailed`)
                  setRejecting(false)
                }}
                className="flex flex-wrap items-center gap-2"
              >
                <input
                  autoFocus
                  required
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  placeholder="Reason, emailed to the applicant"
                  className="input w-72"
                />
                <button type="submit" className="btn-ghost text-red-700">
                  Reject
                </button>
                <button type="button" onClick={() => setRejecting(false)} className="btn-ghost">
                  Cancel
                </button>
              </form>
            )}
            {org.approvalStatus === 'REJECTED' && org.decisionReason && (
              <p className="max-w-xs text-right text-xs text-ink-500">Rejected: {org.decisionReason}</p>
            )}
          </div>
        ) : org.approvalStatus === 'SUSPENDED' ? (
          <button disabled={busyId === org.id} onClick={onReinstateOrg} className="btn-sun gap-1.5 disabled:opacity-40">
            <Check size={14} /> Reinstate organisation
          </button>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            {!['registered', 'registered_as_aggregator'].includes(org.dpgStatus ?? '') && (
              <button
                disabled={busyId === org.id}
                onClick={() =>
                  void act(org.id, () => adminApi.retryAggregatorOrgDpg(org.id), `Registering ${org.name} on aggregator-dpg - refresh in a moment`)
                }
                className="btn-ghost gap-1.5 disabled:opacity-40"
              >
                <RefreshCw size={14} /> Retry DPG
              </button>
            )}
            <button disabled={busyId === org.id} onClick={onSuspendOrg} className="btn-ghost gap-1.5 disabled:opacity-40">
              <Ban size={14} /> Suspend organisation
            </button>
          </div>
        )}
      </div>

      {open &&
        (org.coordinators.length === 0 ? (
          <p className="mt-3 pl-7 text-sm text-ink-500">No coordinators yet - the organisation has not had an invitation accepted.</p>
        ) : (
          <div className="pl-7">
            <CoordinatorTable rows={org.coordinators} busyId={busyId} act={act} />
          </div>
        ))}
    </section>
  )
}

function CoordinatorTable({
  rows,
  busyId,
  act,
}: {
  rows: AdminAggregatorResponse[]
  busyId: string | null
  act: (id: string, action: () => Promise<unknown>, done?: string) => Promise<void>
}) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-ink-400">
          <tr>
            <th className="py-2 pr-3">Coordinator</th>
            <th className="py-2 pr-3">Type</th>
            <th className="py-2 pr-3">Contact</th>
            <th className="py-2 pr-3">Seekers</th>
            <th className="py-2 pr-3">Tutors</th>
            <th className="py-2 pr-3">Signals</th>
            <th className="py-2 pr-3">Aggregator DPG</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {rows.map((c) => (
            <tr key={c.id}>
              <td className="py-2 pr-3 font-medium text-ink-900">
                {c.name}
                <span className="block text-xs font-normal text-ink-400">Joined {new Date(c.createdAt).toLocaleDateString()}</span>
              </td>
              <td className="py-2 pr-3 text-ink-600">{audienceLabel(c.audience)}</td>
              <td className="py-2 pr-3 text-xs text-ink-600">
                {c.coordinatorName ?? '-'}
                <br />
                {c.email ?? ''}
                {c.phone ? ` · ${c.phone}` : ''}
              </td>
              <td className="py-2 pr-3 font-semibold text-ink-900">{c.studentsOnboarded}</td>
              <td className="py-2 pr-3 font-semibold text-ink-900">{c.tutorsOnboarded}</td>
              <td className="py-2 pr-3 text-xs">
                {c.signalsOrgId ? <span className="text-green-700">Connected</span> : <span className="text-amber-700">Not connected</span>}
              </td>
              <td className="py-2 pr-3">
                <DpgBadge status={c.dpgStatus} error={c.dpgSyncError} />
              </td>
              <td className="py-2 pr-3">
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[c.approvalStatus]}`}>{c.approvalStatus}</span>
              </td>
              <td className="py-2 text-right">
                <div className="flex justify-end gap-1">
                  {c.approvalStatus === 'APPROVED' && !c.signalsOrgId && (
                    <button
                      disabled={busyId === c.id}
                      onClick={() => void act(c.id, () => adminApi.connectAggregator(c.id))}
                      className="btn-ghost !py-1.5 text-xs disabled:opacity-40"
                    >
                      Connect
                    </button>
                  )}
                  {c.approvalStatus === 'SUSPENDED' ? (
                    <button
                      disabled={busyId === c.id}
                      onClick={() => void act(c.id, () => adminApi.approveAggregator(c.id), `${c.name} reinstated`)}
                      className="btn-ghost !py-1.5 text-xs disabled:opacity-40"
                    >
                      Reinstate
                    </button>
                  ) : (
                    <button
                      disabled={busyId === c.id}
                      onClick={() => void act(c.id, () => adminApi.suspendAggregator(c.id), `${c.name} suspended`)}
                      className="btn-ghost !py-1.5 text-xs disabled:opacity-40"
                    >
                      Suspend
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Tile({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="card-pad">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-500">
        <span className="text-ink-400">{icon}</span> {label}
      </div>
      <div className="mt-1 text-2xl font-extrabold text-ink-900">{value}</div>
    </div>
  )
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <span className="rounded-full bg-ink-100 px-2.5 py-1 font-semibold text-ink-700">
      {label}: {value}
    </span>
  )
}
